import type {
  MemphisAssetAtRisk,
  MemphisRegionalThreats,
} from './memphisThreatNotification';

export const TNNG_MEMPHIS_THREAT_PROVENANCE =
  'Ting Outages / FloodingBins (Tennessee National Guard demo)';

export const TNNG_MEMPHIS_THRESHOLD_DESCRIPTION =
  'Memphis-area flood and outage thresholds exceeded — Tennessee National Guard resources at risk';

export const TNNG_MEMPHIS_LINKED_EVENT_NAME =
  'Impending Floods and Power Outage in AOR Memphis TN';

export const TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY = 'tnng-memphis-flood-outage';

export const TNNG_MEMPHIS_EVENT_REGION = 'Memphis TN';

export const TNNG_MEMPHIS_EVENT_SUMMARY =
  'Threshold-triggered flood and outage event for Tennessee National Guard resources in the Memphis metro.';

export const TNNG_MEMPHIS_APP_MESSAGE =
  'Likely impact to Tennessee National Guard resources from impending floods and power outages in the Memphis area.';

export const TNNG_MEMPHIS_ORG_ACTION_DESCRIPTION =
  'TNNG Memphis demo — auto-created from flood/outage Response Checklist. Planned / Unassigned.';

export const TNNG_MEMPHIS_RESOURCE_NOTES_PREFIX = 'tnng-memphis-asset:';

export const TNNG_MEMPHIS_ASSETS_LAYER_ID = 'tnng-memphis-assets';
export const TNNG_MEMPHIS_ASSETS_LAYER_TITLE = 'TNNG Memphis assets';

export const TNNG_MEMPHIS_SYMBOLOGY = 'facility';

export type TennesseeMemphisFacility = {
  id: string;
  name: string;
  city: string;
  state: 'TN';
  lon: number;
  lat: number;
  kind: string;
  type: string;
  symbology: string;
  risk: string;
  operationalStatus: MemphisAssetAtRisk['operationalStatus'];
};

/** Facilities inside the Memphis metro used by the TNG-only inbox + seeder. */
export const TNNG_MEMPHIS_FACILITIES: TennesseeMemphisFacility[] = [
  {
    id: 'tnng-memphis-armory',
    name: 'Tennessee National Guard — Memphis Armory',
    city: 'Memphis',
    state: 'TN',
    lon: -90.0074,
    lat: 35.1385,
    kind: 'Facilities',
    type: 'TNNG Armory',
    symbology: 'incidentBase',
    risk: 'Midtown Memphis armory sits in a flood-watch zone with forecast street flooding and grid instability. Confirm high-water vehicles, generators, and unit recall.',
    operationalStatus: 'Partially Operational',
  },
  {
    id: 'tnng-millington-164th',
    name: '164th Airlift Wing / NAS JRB Memphis',
    city: 'Millington',
    state: 'TN',
    lon: -89.8703,
    lat: 35.3567,
    kind: 'Facilities',
    type: 'Air National Guard',
    symbology: 'helibase',
    risk: 'Millington airlift wing faces rising Wolf River tributaries and possible commercial-power loss. Protect flight-line power and stage airlift for civil-support lifts.',
    operationalStatus: 'Partially Operational',
  },
  {
    id: 'tnng-mlgw-ops',
    name: 'MLGW Central Operations',
    city: 'Memphis',
    state: 'TN',
    lon: -90.0519,
    lat: 35.1462,
    kind: 'Facilities',
    type: 'Utility Operations',
    symbology: 'facility',
    risk: 'Downtown MLGW ops is a critical-load node. Forecast flooding and feeder outages will delay restoration for hospitals and TNNG facilities.',
    operationalStatus: 'Partially Operational',
  },
  {
    id: 'tnng-shelby-eoc',
    name: 'Shelby County Emergency Operations Center',
    city: 'Memphis',
    state: 'TN',
    lon: -89.9718,
    lat: 35.1234,
    kind: 'Facilities',
    type: 'Emergency Operations Center',
    symbology: 'emergencyOperationsCenter',
    risk: 'County EOC is the civil-military liaison point. Flooding on approaches and backup-power limits could slow TNNG tasking.',
    operationalStatus: 'Operational',
  },
  {
    id: 'tnng-i40-floodwall',
    name: 'I-40 Hernando de Soto Bridge / Mississippi floodwall',
    city: 'Memphis',
    state: 'TN',
    lon: -90.058,
    lat: 35.152,
    kind: 'Facilities',
    type: 'Transportation / Flood Control',
    symbology: 'incidentCommandPost',
    risk: 'River rise threatens the floodwall and I-40 river crossing. Stage traffic-control and flood-watch posts for west Memphis access.',
    operationalStatus: 'Partially Operational',
  },
  {
    id: 'tnng-memphis-iap-ang',
    name: 'Memphis International Airport — ANG support',
    city: 'Memphis',
    state: 'TN',
    lon: -89.9767,
    lat: 35.0424,
    kind: 'Facilities',
    type: 'Airport / ANG Support',
    symbology: 'helispot',
    risk: 'Airport ANG support area is in an outage cluster. Protect fuel, comms, and rotary-wing staging for flood reconnaissance.',
    operationalStatus: 'Partially Operational',
  },
];

export const TNNG_MEMPHIS_RESPONSE_CHECKLIST_ITEMS = [
  {
    id: 'tnng-checklist-activate-joc',
    label:
      'Activate the Tennessee National Guard JOC and notify the Adjutant General and Memphis-area unit commanders.',
  },
  {
    id: 'tnng-checklist-stage-vehicles',
    label:
      'Stage high-water vehicles and generators at the Memphis Armory and 164th Airlift Wing in Millington.',
  },
  {
    id: 'tnng-checklist-mlgw-loads',
    label:
      'Coordinate with MLGW on outage restoration priorities and critical-load lists for TNNG and public-safety sites.',
  },
  {
    id: 'tnng-checklist-flood-watch',
    label:
      'Establish flood-watch posts on the Wolf River / Mississippi floodwall and the I-40 Hernando de Soto crossing.',
  },
  {
    id: 'tnng-checklist-eoc-liaison',
    label:
      'Open a Shelby County EOC liaison and stand up an incident workspace for Memphis flood and power-outage response.',
  },
] as const;

export const TNNG_MEMPHIS_RESPONSE_CHECKLIST_LABELS =
  TNNG_MEMPHIS_RESPONSE_CHECKLIST_ITEMS.map((item) => item.label);

export const TNNG_MEMPHIS_THREAT_THRESHOLDS = [
  {
    input: 'Flood extent',
    region: 'FloodingBins Demo — Memphis metro',
    threshold: '≥ 1 flood bin intersecting TNNG facilities',
    observed: `${TNNG_MEMPHIS_FACILITIES.length} Memphis-area facilities in flood watch`,
  },
  {
    input: 'Power outages',
    region: 'Ting Outages TN Floods AOI',
    threshold: '≥ 1 feeder outage affecting TNNG or MLGW critical loads',
    observed: 'Outage cluster over Midtown, airport, and river corridor',
  },
] as const;

export const TNNG_MEMPHIS_RELATED_EVENTS = [
  TNNG_MEMPHIS_LINKED_EVENT_NAME,
  TNNG_MEMPHIS_THRESHOLD_DESCRIPTION,
  ...TNNG_MEMPHIS_THREAT_THRESHOLDS.map(
    (row) =>
      `${row.input} in ${row.region}: ${row.observed} (threshold ${row.threshold})`
  ),
];

export const TNNG_MEMPHIS_DEMO_INCIDENT_TYPE = 'Flood';

export const TNNG_MEMPHIS_DEMO_AOR = {
  value: 'demo-tnng-memphis-flood-aor',
  label: 'TNNG Memphis metro — flood / outage',
} as const;

export function tnngMemphisResourceNotesMarker(facilityId: string): string {
  return `${TNNG_MEMPHIS_RESOURCE_NOTES_PREFIX}${facilityId}`;
}

export function isTnngMemphisResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(TNNG_MEMPHIS_RESOURCE_NOTES_PREFIX);
}

export function tnngFacilityIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes?.startsWith(TNNG_MEMPHIS_RESOURCE_NOTES_PREFIX)) return undefined;
  const id = notes.slice(TNNG_MEMPHIS_RESOURCE_NOTES_PREFIX.length);
  return id || undefined;
}

export function isTnngMemphisOrgActionName(
  name: string | null | undefined
): boolean {
  return (
    !!name &&
    (TNNG_MEMPHIS_RESPONSE_CHECKLIST_LABELS as readonly string[]).includes(name)
  );
}

function facilityToAsset(
  facility: TennesseeMemphisFacility
): MemphisAssetAtRisk {
  return {
    id: facility.id,
    resource: `${facility.name} (${facility.city}, ${facility.state})`,
    risk: facility.risk,
    location: [facility.lon, facility.lat],
    operationalStatus: facility.operationalStatus,
  };
}

export function getTennesseeMemphisRegionalThreats(): MemphisRegionalThreats {
  return {
    region: 'Tennessee National Guard — Memphis metro',
    description:
      'Impending Mississippi / Wolf River flooding and feeder outages place the following Tennessee National Guard and supporting facilities at elevated risk:',
    threats: TNNG_MEMPHIS_FACILITIES.map(facilityToAsset),
    responseChecklist: TNNG_MEMPHIS_RESPONSE_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

export function tennesseeMemphisCreateIncidentPrefill(eventId?: string) {
  return {
    initialRelatedEvents: eventId
      ? [eventId]
      : [...TNNG_MEMPHIS_RELATED_EVENTS],
    initialIncidentTypes: [TNNG_MEMPHIS_DEMO_INCIDENT_TYPE],
    initialAorSelections: [TNNG_MEMPHIS_DEMO_AOR],
  };
}
